import {
  formatAndSetBackgroundOptions,
  formatAndSetIncentiveProducts,
  getShootData,
  setInterstitialProducts,
  setPersonalizationProduct,
  setPreCartOfferProducts,
  setRetouchingProduct,
  setRetouchingProducts,
  setTriggersPreCartOfferProducts,
} from './action_shoot';
import { setAdditionalData } from '../reducers/additionalDataSlice';
import { shootLoadParamsToRequest } from '../components/v3/dashboard/galleryOrderContext';

// Loading a shoot is more than one HTTP call: `state.shoot` is only usable
// once the response has been unpacked into the slices the ordering UI reads
// (backgrounds keyed by id, incentives grouped by threshold, retouching /
// personalization / interstitial / pre-cart-offer products) and
// `additionalData` names the student being shopped for. DashboardGalleries
// did all of that inline; the cart's bonus re-add (ENG-2043, re-add Tier 3)
// needs the identical sequence for an order whose shoot is NOT the active
// one, so it lives here and both callers dispatch it.
//
// Deliberately NOT included: the yearbook-selection and prepay-guard
// branches around the dashboard's call. Those decide whether a NEW order may
// be started from a gallery; loading a shoot's context does not.

// The Redux store is plain JS (redux-promise + thunks, untyped reducers), so
// dispatch is modelled loosely here rather than pretending to a typed store —
// the same compromise the `state: any` selectors make.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ShootContextDispatch = (action: any) => any;

interface BackgroundOption {
  id: number | string;
}

interface BackgroundCategory {
  background_images: BackgroundOption[];
}

interface IncentiveProductRow {
  incentive_threshold: number | string;
}

export interface LoadedShootData {
  contains_background_categories?: boolean;
  background_options?: BackgroundOption[] | BackgroundCategory[];
  incentive_products?: IncentiveProductRow[];
  retouching_product?: unknown;
  retouching_products?: unknown;
  personalization_product?: unknown;
  interstitial_products?: unknown;
  pre_cart_offer_products?: unknown;
  triggers_pre_cart_offer_products?: unknown;
  errors?: unknown;
}

export interface ShootContextOrder {
  additionalData?: Record<string, unknown> | null;
  event?: { id?: number | string | null; name?: string | null } | null;
  student?: { id?: number | string | null } | null;
  shootLoadParams?: Record<string, unknown> | null;
}

// TODO: determine why we need this formatting logic... can we just return the
// backgrounds and incentive products formatted as needed?
const formattedBackgroundOptions = (
  containsBackgroundCategories: boolean | undefined,
  backgroundOptions: BackgroundOption[] | BackgroundCategory[] | undefined,
): Record<string, BackgroundOption> => {
  const formatted: Record<string, BackgroundOption> = {};

  if (containsBackgroundCategories) {
    ((backgroundOptions || []) as BackgroundCategory[]).forEach(
      (backgroundSet) => {
        backgroundSet.background_images.forEach((backgroundOption) => {
          formatted[backgroundOption.id] = backgroundOption;
        });
      },
    );
  } else {
    ((backgroundOptions || []) as BackgroundOption[]).forEach(
      (backgroundOption) => {
        formatted[backgroundOption.id] = backgroundOption;
      },
    );
  }

  return formatted;
};

const formattedIncentiveProducts = (
  incentiveProducts: IncentiveProductRow[] | undefined,
): Record<string, IncentiveProductRow[]> => {
  const formatted: Record<string, IncentiveProductRow[]> = {};

  (incentiveProducts || []).forEach((incentiveProduct) => {
    if (!formatted[incentiveProduct.incentive_threshold]) {
      formatted[incentiveProduct.incentive_threshold] = [];
    }

    formatted[incentiveProduct.incentive_threshold].push(incentiveProduct);
  });

  return formatted;
};

// Put a freshly loaded shoot's payload into the store. `additionalDataContext`
// is whatever identifies the order being shopped (the dashboard's gallery
// context; for a cart re-add, the target order's own additionalData plus its
// student) and is applied LAST, so nothing reads a half-populated shoot with
// the new student already selected.
export const applyLoadedShootData =
  (
    shootData: LoadedShootData,
    additionalDataContext: Record<string, unknown>,
  ) =>
  (dispatch: ShootContextDispatch): void => {
    dispatch(
      formatAndSetBackgroundOptions(
        formattedBackgroundOptions(
          shootData.contains_background_categories,
          shootData.background_options,
        ),
      ),
    );
    dispatch(
      formatAndSetIncentiveProducts(
        formattedIncentiveProducts(shootData.incentive_products),
      ),
    );

    dispatch(setRetouchingProduct(shootData.retouching_product));
    dispatch(setRetouchingProducts(shootData.retouching_products));
    dispatch(setPersonalizationProduct(shootData.personalization_product));
    dispatch(setInterstitialProducts(shootData.interstitial_products));
    dispatch(setPreCartOfferProducts(shootData.pre_cart_offer_products));
    dispatch(
      setTriggersPreCartOfferProducts(
        shootData.triggers_pre_cart_offer_products,
      ),
    );

    dispatch(setAdditionalData(additionalDataContext));
  };

// Make an order's own shoot the active one, from the cart. The order stashed
// what the lookup needs on its first add (`shootLoadParams`: student id, event
// id and gallery KEY — never a bare gallery id), so the re-add of a bonus
// earned at another school can be configured in that school's context.
//
// Rejects when the gallery no longer loads (network error, or an `errors`
// payload from a gallery that has closed) so the caller can keep the parent on
// the cart and say so, rather than routing into a configurator backed by the
// wrong shoot.
export const loadShootContextForOrder =
  (order: ShootContextOrder) =>
  async (dispatch: ShootContextDispatch): Promise<LoadedShootData> => {
    const response = await dispatch(
      getShootData(shootLoadParamsToRequest(order?.shootLoadParams)),
    );
    const shootData: LoadedShootData | undefined = response?.payload?.data;

    if (!shootData || shootData.errors) {
      throw new Error('Unable to load the shoot context for this order');
    }

    dispatch(
      applyLoadedShootData(shootData, {
        ...(order.additionalData || {}),
        selectedStudent: order.student,
        studentId: order.student?.id ?? null,
      }),
    );

    return shootData;
  };
